/*
  The cost assumptions, ported from renderAssumptions (orgchart.html:6787-6845).

  Five numbers behind every cost figure in the tool, and until now the client could read them and not
  one of them could be changed — four panels printed "see Assumptions" pointing at a surface that did
  not exist. Two are open-ended grids keyed by the client's own country and grade values.

  Three deliberate divergences from the original, each a defect rather than a feature:

  1. A BLANK SCALAR IS NOT A ZERO. The original's default-severance and hiring-cost boxes accept an
     emptied field as `Number('') === 0` and commit it (:6823, :6831); only the multiplier escapes,
     because :6816 happens to test `v <= 0`. Clearing a box is how people undo a keystroke, not how
     they declare a no-severance regime, so a blank restores and dispatches nothing. Zero has to be
     typed.
  2. ORPHAN TIERS ARE VISIBLE. The original sources its rows from the current population, so a tier
     for a country nobody works in any more vanishes from the editor while `severanceMonths` goes on
     reading it — and it silently reprices the moment someone from that country is hired. Here the
     grid renders the union of the population and the stored keys, tags what has nobody, and offers a
     clear button. Never an auto-prune: that would be a mutation nothing logged.
  3. A LIVE TOTAL. The one-off severance and hiring figures are recomputed against the pending value
     as you type, so the effect of a change is visible without closing the modal and hunting for it.

  Everything else is held to the engine. Grid rows come from `assumptionGridKeys` — NOT
  `getDistinctValues`, which resolves scenario renames, walks build-ups and folds blanks into
  "(No Country)", each of which yields a row that is stored, logged, exported and prices nothing.
*/
import { useMemo, useState } from 'react';
import type { Command } from '@integrate/contracts';
import {
  assumptionGridKeys,
  COLUMN_FIELDS,
  computeOneOffCosts,
  fmtMoney,
  type SeparationState,
} from '@integrate/core';
import { LiveField } from './live-input.js';
import { Modal } from '../ui/Modal.js';

type GridField = 'country' | 'seniority' | 'currency';
type GridStorageKey =
  | 'severanceByCountry'
  | 'severanceBySeniority'
  | 'fxRates'
  | 'consultationMonthsByCountry'
  | 'loadedCostMultiplierByCountry'
  | 'closeMonthByCountry';

/**
 * Row source → default storage. No longer one-to-one: the consultation grid shares the country
 * ROWS with the severance grid and stores elsewhere, so the callers below pass the storage key
 * explicitly and this map is only the severance-era default.
 */
const GRID_KEY: Record<GridField, GridStorageKey> = {
  country: 'severanceByCountry',
  seniority: 'severanceBySeniority',
  currency: 'fxRates',
};

function columnLabel(key: GridField): string {
  return COLUMN_FIELDS.find((c) => c.key === key)?.label ?? key;
}

/**
 * The stored tier for one key, as the input box wants it.
 *
 * All three of the engine's "inherit" encodings render blank (`cost.ts:47-51`), and a stored `'3'`
 * from an older project file renders as 3 rather than as something unrenderable.
 */
function tierValue(grid: Record<string, number | string>, key: string): string {
  if (!Object.prototype.hasOwnProperty.call(grid, key)) return '';
  const raw = grid[key];
  if (raw == null || String(raw).trim() === '') return '';
  return String(Number(raw));
}

export function AssumptionsEditor({
  state,
  readOnly,
  onDispatch,
  onClose,
}: {
  state: SeparationState;
  readOnly: boolean;
  onDispatch(command: Command): void;
  onClose(): void;
}): JSX.Element {
  const a = state.assumptions;

  /*
    What the totals strip is priced at.

    Held only while a box has focus and an uncommitted value in it, and cleared on every commit —
    there is no local mirror of committed state, so a change another tab makes cannot be shadowed by
    a stale copy of the number this tab last saw.
  */
  const [pending, setPending] = useState<{ key: string; subKey: string | null; value: number } | null>(null);

  const preview = useMemo(() => {
    if (!pending) return computeOneOffCosts(state);
    const next = { ...a, severanceByCountry: { ...a.severanceByCountry }, severanceBySeniority: { ...a.severanceBySeniority }, fxRates: { ...a.fxRates }, consultationMonthsByCountry: { ...a.consultationMonthsByCountry }, loadedCostMultiplierByCountry: { ...a.loadedCostMultiplierByCountry }, closeMonthByCountry: { ...a.closeMonthByCountry } };
    if (pending.subKey === null) {
      (next as unknown as Record<string, number>)[pending.key] = pending.value;
    } else {
      (next[pending.key as 'severanceByCountry'] as Record<string, number>)[pending.subKey] = pending.value;
    }
    return computeOneOffCosts({ ...state, assumptions: next });
  }, [state, a, pending]);

  const commitScalar = (key: 'loadedCostMultiplier' | 'severanceMonthsDefault' | 'hiringCostPct' | 'retentionPct', raw: string): void => {
    setPending(null);
    // A blank box is a cleared keystroke, not a declaration of zero — see divergence 1 above.
    if (raw.trim() === '') return;
    const value = Number(raw);
    if (!Number.isFinite(value) || value === Number(a[key])) return;
    onDispatch({ type: 'setAssumption', key, subKey: null, value });
  };

  const commitTier = (key: GridStorageKey, subKey: string, raw: string): void => {
    setPending(null);
    const before = tierValue(a[key], subKey);
    if (raw.trim() === '') {
      if (before === '') return;
      onDispatch({ type: 'setAssumption', key, subKey, value: null });
      return;
    }
    const value = Number(raw);
    if (!Number.isFinite(value) || (before !== '' && value === Number(before))) return;
    onDispatch({ type: 'setAssumption', key, subKey, value });
  };

  return (
    <Modal
      testId="assumptions-modal"
      title="Cost assumptions"
      onClose={onClose}
      footer={
        <>
          <div className="asm-totals">
            <Total testId="asm-preview-severance" label="Severance" value={preview.severance} />
            <Total testId="asm-preview-hiring" label="Hiring" value={preview.hiring} />
            <Total testId="asm-preview-total" label="One-off total" value={preview.total} />
          </div>
          <span className="spacer" />
          <button className="primary" onClick={onClose}>
            Done
          </button>
        </>
      }
    >
      <p className="muted" style={{ marginTop: 0 }}>
        {/*
          The three surfaces, named because they now all exist. This sentence promised a "one-off
          register" while the platform had only a hiring-total row; it is the shared one-off table on
          the Separation summary and the Phase bridge, and it carries severance as well as hiring.
        */}
        Used by the economics surfaces only — TSA economics, stranded cost, and the one-off table on
        the Separation summary and Phase bridge tabs. Org-chart salary figures always stay raw. Assumptions are <strong>global across every
        scenario</strong> — changing one reprices them all.
      </p>

      <Scalar
        testId="asm-multiplier"
        label="Loaded-cost multiplier (on salary)"
        value={String(a.loadedCostMultiplier)}
        min={0.1}
        step={0.05}
        disabled={readOnly}
        help="Annual loaded cost = salary × multiplier (employer charges, benefits, workplace overheads). Typical: 1.2–1.5."
        onPreview={(v) => setPending({ key: 'loadedCostMultiplier', subKey: null, value: v })}
        onCommit={(raw) => commitScalar('loadedCostMultiplier', raw)}
      />
      <Scalar
        testId="asm-default-months"
        label="Default severance (months of salary)"
        value={String(a.severanceMonthsDefault)}
        min={0}
        step={0.5}
        disabled={readOnly}
        help="One-off cost of a departure = months × monthly base salary. Deliberately unloaded: employer overheads stop anyway. Override per country or grade below where redundancy regimes differ."
        onPreview={(v) => setPending({ key: 'severanceMonthsDefault', subKey: null, value: v })}
        onCommit={(raw) => commitScalar('severanceMonthsDefault', raw)}
      />
      <Scalar
        testId="asm-hiring-pct"
        label="Hiring cost (% of annual salary, build-ups)"
        value={String(a.hiringCostPct)}
        min={0}
        step={1}
        disabled={readOnly}
        help="One-off cost of each planned position (recruiting fees, onboarding) = % × its annual base salary. Counted in the one-off table alongside severance. Typical: 15–30%."
        onPreview={(v) => setPending({ key: 'hiringCostPct', subKey: null, value: v })}
        onCommit={(raw) => commitScalar('hiringCostPct', raw)}
      />
      <Scalar
        testId="asm-retention-pct"
        label="Retention to exit (% of annual salary)"
        value={String(a.retentionPct)}
        min={0}
        step={1}
        disabled={readOnly}
        help="One-off retention for the people who must stay to their own departure — carve-out Day 2 exits and stranded rightsizings. Severance without retention reads as one-sided. Typical: 10–25%; 0 prices nothing."
        onPreview={(v) => setPending({ key: 'retentionPct', subKey: null, value: v })}
        onCommit={(raw) => commitScalar('retentionPct', raw)}
      />

      <Grid
        field="country"
        title={`Severance by ${columnLabel('country').toLowerCase()} (overrides the default)`}
        valueHeader="Severance months (blank = inherit)"
        state={state}
        readOnly={readOnly}
        onPreview={(subKey, value) => setPending({ key: GRID_KEY.country, subKey, value })}
        onCommit={(subKey, raw) => commitTier('severanceByCountry', subKey, raw)}
        onClear={(subKey) => commitTier('severanceByCountry', subKey, '')}
      />
      <Grid
        field="seniority"
        title={`Severance by ${columnLabel('seniority').toLowerCase()} (overrides country and the default)`}
        valueHeader="Severance months (blank = inherit)"
        state={state}
        readOnly={readOnly}
        onPreview={(subKey, value) => setPending({ key: GRID_KEY.seniority, subKey, value })}
        onCommit={(subKey, raw) => commitTier('severanceBySeniority', subKey, raw)}
        onClear={(subKey) => commitTier('severanceBySeniority', subKey, '')}
      />

      {/*
        FX, the third grid: same rows-from-the-roster mechanics, its own meaning. A tier's blank is
        × 1 (the salary is already in the reporting currency), not an inheritance chain — the header
        and the handler's own wording both say so, and zero is refused there rather than here.
      */}
      <h5 className="asm-grid-h">Reporting currency</h5>
      <div className="field" style={{ marginTop: 4 }}>
        <LiveField
          data-testid="asm-reporting-currency"
          type="text"
          value={a.reportingCurrency ?? ''}
          disabled={readOnly}
          onInput={() => {}}
          onCommit={(text) => {
            const next = text.trim();
            if (next === (a.reportingCurrency ?? '')) return;
            onDispatch({ type: 'setAssumption', key: 'reportingCurrency', subKey: null, value: next === '' ? null : next });
          }}
        />
        <div className="muted asm-help" style={{ marginTop: 3 }}>
          A label for what converted figures are in (e.g. EUR) — the rates below do the arithmetic.
          Blank is fine for a single-currency roster.
        </div>
      </div>
      <Grid
        field="currency"
        title={`FX rates by ${columnLabel('currency').toLowerCase()} (into the reporting currency)`}
        valueHeader="Rate (blank = × 1)"
        step={0.01}
        state={state}
        readOnly={readOnly}
        onPreview={(subKey, value) => setPending({ key: GRID_KEY.currency, subKey, value })}
        onCommit={(subKey, raw) => commitTier('fxRates', subKey, raw)}
        onClear={(subKey) => commitTier('fxRates', subKey, '')}
      />

      {/*
        The calendar. Day 1 gets a date — a label like the reporting currency: the arithmetic stays
        on month indices, the reading surfaces print real months — and consultation lead times get
        the third country-rowed grid, feeding the diagnostics that flag a rightsizing planned inside
        its country's consultation window.
      */}
      <h5 className="asm-grid-h">Day 1 date</h5>
      <div className="field" style={{ marginTop: 4 }}>
        <LiveField
          data-testid="asm-day1-date"
          type="date"
          value={a.day1Date ?? ''}
          disabled={readOnly}
          onInput={() => {}}
          onCommit={(text) => {
            const next = text.trim();
            if (next === (a.day1Date ?? '')) return;
            onDispatch({ type: 'setAssumption', key: 'day1Date', subKey: null, value: next === '' ? null : next });
          }}
        />
        <div className="muted asm-help" style={{ marginTop: 3 }}>
          Dates the month clock: charts and exports print real months instead of M6. Every stored
          month stays an index, so clearing this changes no number.
        </div>
      </div>
      <Grid
        field="country"
        gridKey="consultationMonthsByCountry"
        title={`Consultation lead time by ${columnLabel('country').toLowerCase()}`}
        valueHeader="Months before a departure can take effect (blank = none)"
        state={state}
        readOnly={readOnly}
        onPreview={(subKey, value) => setPending({ key: 'consultationMonthsByCountry', subKey, value })}
        onCommit={(subKey, raw) => commitTier('consultationMonthsByCountry', subKey, raw)}
        onClear={(subKey) => commitTier('consultationMonthsByCountry', subKey, '')}
      />
      <Grid
        field="country"
        gridKey="closeMonthByCountry"
        title={`Local close month by ${columnLabel('country').toLowerCase()}`}
        valueHeader="Whole months after Day 1 the country closes (blank = Day 1)"
        step={1}
        state={state}
        readOnly={readOnly}
        onPreview={(subKey, value) => setPending({ key: 'closeMonthByCountry', subKey, value })}
        onCommit={(subKey, raw) => commitTier('closeMonthByCountry', subKey, raw)}
        onClear={(subKey) => commitTier('closeMonthByCountry', subKey, '')}
      />
      <Grid
        field="country"
        gridKey="loadedCostMultiplierByCountry"
        title={`Loaded-cost multiplier by ${columnLabel('country').toLowerCase()} (overrides the global)`}
        valueHeader="Multiplier (blank = global)"
        step={0.05}
        state={state}
        readOnly={readOnly}
        onPreview={(subKey, value) => setPending({ key: 'loadedCostMultiplierByCountry', subKey, value })}
        onCommit={(subKey, raw) => commitTier('loadedCostMultiplierByCountry', subKey, raw)}
        onClear={(subKey) => commitTier('loadedCostMultiplierByCountry', subKey, '')}
      />
    </Modal>
  );
}

function Total({ testId, label, value }: { testId: string; label: string; value: number }): JSX.Element {
  return (
    <div className="asm-total">
      <div className="lbl">{label}</div>
      <div className="val" data-testid={testId}>
        {fmtMoney(value)}
      </div>
    </div>
  );
}

/**
 * One scalar assumption: commit-on-blur, with the totals strip tracking every keystroke.
 *
 * Uncontrolled while you type, so a round trip mid-edit cannot yank the caret, and resynced by
 * `useLiveInput` when the committed value moves — which is NOT what `key={value}` did here before. That
 * remounted the box, and a remount is a destroyed DOM node: a colleague committing the same assumption
 * threw away whatever you were part-way through typing. See `live-input.tsx`.
 */
function Scalar({
  testId,
  label,
  value,
  min,
  step,
  disabled,
  help,
  onPreview,
  onCommit,
}: {
  testId: string;
  label: string;
  value: string;
  min: number;
  step: number;
  disabled: boolean;
  help: string;
  onPreview(value: number): void;
  onCommit(raw: string): void;
}): JSX.Element {
  return (
    <div className="field" style={{ marginTop: 10 }}>
      <label>{label}</label>
      <LiveField
        data-testid={testId}
        type="number"
        value={value}
        min={min}
        step={step}
        disabled={disabled}
        onInput={(text) => {
          const n = Number(text);
          if (text.trim() !== '' && Number.isFinite(n)) onPreview(n);
        }}
        onCommit={(text) => onCommit(text)}
      />
      <div className="muted asm-help" style={{ marginTop: 3 }}>
        {help}
      </div>
    </div>
  );
}

/**
 * One severance grid: every key the cost function could match, plus every key it already holds.
 *
 * The union is the point. Rows sourced from the population alone hide a tier that is still being
 * priced (`cost.ts:36-44`), which is the same class of invisibility the app's own diagnostics flag
 * for orphaned bucket metadata (`orgchart.html:3252`).
 */
function Grid({
  field,
  gridKey,
  title,
  valueHeader,
  step = 0.5,
  state,
  readOnly,
  onPreview,
  onCommit,
  onClear,
}: {
  /** Where the ROWS come from — a person field the roster carries. */
  field: GridField;
  /** Where the tiers are STORED. Defaults to the severance-era pairing. */
  gridKey?: GridStorageKey;
  title: string;
  /** What a tier's number MEANS here — months inherit down a chain, a rate falls back to × 1. */
  valueHeader: string;
  step?: number;
  state: SeparationState;
  readOnly: boolean;
  onPreview(subKey: string, value: number): void;
  onCommit(subKey: string, raw: string): void;
  onClear(subKey: string): void;
}): JSX.Element {
  const storage = gridKey ?? GRID_KEY[field];
  const grid = state.assumptions[storage];

  const { rows, orphans } = useMemo(() => {
    const present = assumptionGridKeys(state, field);
    const known = new Set(present);
    const extra = Object.keys(grid)
      .filter((k) => !known.has(k))
      .sort((x, y) => x.localeCompare(y));
    return { rows: [...present, ...extra], orphans: new Set(extra) };
  }, [state, field, grid]);

  const label = columnLabel(field);

  return (
    <>
      <h5 className="asm-grid-h">{title}</h5>
      {rows.length === 0 ? (
        <p className="muted asm-help" style={{ margin: 0 }} data-testid={`asm-${field}-empty`}>
          No {label.toLowerCase()} data in the employee list.
        </p>
      ) : (
        <div className="table-scroll">
          <table className="tsa-table">
            <thead>
              <tr>
                <th>{label}</th>
                <th className="num">{valueHeader}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((key) => {
                const orphan = orphans.has(key);
                return (
                  <tr key={key} data-testid={orphan ? `asm-orphan-${storage}-${key}` : undefined}>
                    <td>
                      {key}
                      {orphan && (
                        <span className="pill" title="This tier is still priced, but nobody in the data carries this value">
                          no one in the data
                        </span>
                      )}
                    </td>
                    <td className="num">
                      <Tier
                        testId={`asm-${storage === GRID_KEY[field] ? field : storage}-${key}`}
                        value={tierValue(grid, key)}
                        step={step}
                        disabled={readOnly}
                        onPreview={(n) => onPreview(key, n)}
                        onCommit={(raw) => onCommit(key, raw)}
                      />
                    </td>
                    <td>
                      {orphan && (
                        <button
                          data-testid={`asm-clear-${storage === GRID_KEY[field] ? field : storage}-${key}`}
                          disabled={readOnly}
                          onClick={() => onClear(key)}
                          title="Remove this tier"
                        >
                          Clear
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/**
 * One tier's months.
 *
 * A component rather than an inline `<input>` because `useLiveInput` is a hook and the grid renders these
 * in a `map` — and the reason it needs the hook is the same as `Scalar`'s: this box carried
 * `key={tierValue(...)}`, so a colleague repricing the same country threw away the number you were
 * typing into it.
 */
function Tier({
  testId,
  value,
  step,
  disabled,
  onPreview,
  onCommit,
}: {
  testId: string;
  value: string;
  step: number;
  disabled: boolean;
  onPreview(value: number): void;
  onCommit(raw: string): void;
}): JSX.Element {
  return (
    <LiveField
      data-testid={testId}
      type="number"
      value={value}
      min={0}
      step={step}
      disabled={disabled}
      onInput={(text) => {
        const n = Number(text);
        if (text.trim() !== '' && Number.isFinite(n)) onPreview(n);
      }}
      onCommit={(text) => onCommit(text)}
    />
  );
}
